/**
 * Контекст авторизации.
 *
 * Профиль загружается один раз при старте приложения. Гость — это не
 * ошибка, а нормальное состояние: лендинг и оверлей работают без входа,
 * поэтому 401 от `/auth/me` обрабатывается как «пользователь не вошёл».
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { UserProfile } from '@gpstracker/shared';
import { ApiError, api } from './api';

interface AuthState {
  user: UserProfile | null;
  /** true, пока профиль ещё не проверен: UI не должен мигать входом. */
  loading: boolean;
  /** Ошибка, не связанная с отсутствием сессии. */
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (returnTo?: string) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.me();
      setState({ user, loading: false, error: null });
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) {
        setState({ user: null, loading: false, error: null });
        return;
      }
      setState({
        user: null,
        loading: false,
        error: error instanceof Error ? error.message : 'Не удалось проверить сессию',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Вход — полный переход браузера, а не fetch.
   * OAuth требует редиректа на страницу согласия Twitch.
   */
  const login = useCallback((returnTo = '/dashboard') => {
    window.location.href = api.loginUrl(returnTo);
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setState({ user: null, loading: false, error: null });
    window.location.href = '/';
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refresh }),
    [state, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth вызван вне AuthProvider');
  }
  return context;
}
